import { createSignal, For, Index, Show } from "solid-js";
import Text from "./Text";
import { DateRangePills } from "./DateRangePills";
import { useTranslation } from "../hooks/useTranslation";
import { formatDateTimeShort } from "../services/dateFormat";
import styles from "./ScheduleEditor.module.css";

export type ScheduleKind = "times" | "interval" | "weekly" | "monthly";

export interface ScheduleDraft {
    kind: ScheduleKind;
    // DayOfWeek values as the server reads them: 0 is Sunday.
    days: number[];
    daysOfMonth: number[];
    minutesOfDay: number[];
    everyMinutes: number;
    fromMinute: number;
    untilMinute: number;
    jitterMinutes: number;
}

const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAYS = [1, 2, 3, 4, 5];
const WEEKEND = [6, 0];
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, index) => index + 1);
const LAST_MINUTE = 1439;
const MAX_JITTER = 60;
const INTERVAL_PRESETS = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 360, 480, 720];

export const defaultSchedule = (): ScheduleDraft => ({
    kind: "times",
    days: [...MONDAY_FIRST],
    daysOfMonth: [1],
    minutesOfDay: [20 * 60],
    everyMinutes: 60,
    fromMinute: 0,
    untilMinute: LAST_MINUTE,
    jitterMinutes: 0,
});

export function scheduleToJson(draft: ScheduleDraft): string {
    const days = [...draft.days].sort((a, b) => a - b);
    const minutesOfDay = [...draft.minutesOfDay].sort((a, b) => a - b);
    switch (draft.kind) {
        case "times":
        case "weekly": return JSON.stringify({ kind: draft.kind, days, minutesOfDay, jitterMinutes: draft.jitterMinutes });
        case "monthly": return JSON.stringify({ kind: "monthly", daysOfMonth: [...draft.daysOfMonth].sort((a, b) => a - b), minutesOfDay, jitterMinutes: draft.jitterMinutes });
        default: return JSON.stringify({ kind: "interval", days, everyMinutes: draft.everyMinutes, fromMinute: draft.fromMinute, untilMinute: draft.untilMinute, jitterMinutes: draft.jitterMinutes });
    }
}

export function parseSchedule(scheduleJson: string): ScheduleDraft {
    const fallback = defaultSchedule();
    try {
        const stored = JSON.parse(scheduleJson || "{}") ?? {};
        return {
            kind: stored.kind === "interval" || stored.kind === "weekly" || stored.kind === "monthly" ? stored.kind : "times",
            days: Array.isArray(stored.days) ? stored.days : fallback.days,
            daysOfMonth: Array.isArray(stored.daysOfMonth) ? stored.daysOfMonth : fallback.daysOfMonth,
            minutesOfDay: Array.isArray(stored.minutesOfDay) ? stored.minutesOfDay : fallback.minutesOfDay,
            everyMinutes: Number(stored.everyMinutes) || fallback.everyMinutes,
            fromMinute: Number(stored.fromMinute) || 0,
            untilMinute: stored.untilMinute == null ? LAST_MINUTE : Number(stored.untilMinute),
            jitterMinutes: Number(stored.jitterMinutes) || 0,
        };
    } catch {
        return fallback;
    }
}

export const minutesToClock = (minutes: number) =>
    `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

const clockToMinutes = (clock: string): number | null => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(clock);
    if (!match) return null;
    const minutes = Number(match[1]) * 60 + Number(match[2]);
    return minutes >= 0 && minutes <= LAST_MINUTE ? minutes : null;
};

const sameDays = (days: number[], expected: number[]) =>
    days.length === expected.length && expected.every(day => days.includes(day));

export function describeSchedule(draft: ScheduleDraft, t: (key: string, ...args: any[]) => string): string {
    const times = [...draft.minutesOfDay].sort((a, b) => a - b).map(minutesToClock).join(", ");

    if (draft.kind === "weekly") {
        const weekdays = MONDAY_FIRST.filter(day => draft.days.includes(day)).map(day => t(`AUTOMATION_DAY_LONG_${day}`)).join(", ");
        return t("AUTOMATION_SCHEDULE_SUMMARY_WEEKLY", times, weekdays);
    }

    if (draft.kind === "monthly") {
        const daysOfMonth = [...draft.daysOfMonth].sort((a, b) => a - b).map(day => t("AUTOMATION_SCHEDULE_DAY_OF_MONTH", day)).join(", ");
        return t("AUTOMATION_SCHEDULE_SUMMARY_MONTHLY", times, daysOfMonth);
    }

    const days = sameDays(draft.days, MONDAY_FIRST) ? t("AUTOMATION_SCHEDULE_ON_EVERY_DAY")
        : sameDays(draft.days, WEEKDAYS) ? t("AUTOMATION_SCHEDULE_ON_WEEKDAYS")
        : sameDays(draft.days, WEEKEND) ? t("AUTOMATION_SCHEDULE_ON_WEEKEND")
        : MONDAY_FIRST.filter(day => draft.days.includes(day)).map(day => t(`AUTOMATION_DAY_SHORT_${day}`)).join(", ");

    if (draft.kind === "times") {
        return t("AUTOMATION_SCHEDULE_SUMMARY_TIMES", times, days);
    }

    return draft.fromMinute === 0 && draft.untilMinute === LAST_MINUTE
        ? t("AUTOMATION_SCHEDULE_SUMMARY_INTERVAL_ALL_DAY", intervalLabel(draft.everyMinutes, t), days)
        : t("AUTOMATION_SCHEDULE_SUMMARY_INTERVAL", intervalLabel(draft.everyMinutes, t),
            t("AUTOMATION_SCHEDULE_WINDOW", minutesToClock(draft.fromMinute), minutesToClock(draft.untilMinute)), days);
}

function intervalLabel(minutes: number, t: (key: string, ...args: any[]) => string): string {
    if (minutes === 60) return t("AUTOMATION_SCHEDULE_EVERY_HOUR");
    return minutes % 60 === 0
        ? t("AUTOMATION_SCHEDULE_EVERY_HOURS", minutes / 60)
        : t("AUTOMATION_SCHEDULE_EVERY_MINUTES", minutes);
}

interface Props {
    value: ScheduleDraft;
    onChange: (next: ScheduleDraft) => void;
    upcomingRuns: readonly string[];
    error: string | null;
    previewIncludesJitter: boolean;
    hideJitter?: boolean;
    upcomingKey?: string;
    clockHintKey?: string;
}

// No shared weekday or time-list picker exists to extend; the kind toggle reuses DateRangePills.
export default function ScheduleEditor(props: Props) {
    const { t } = useTranslation();
    const [pendingTime, setPendingTime] = createSignal("08:00");

    const patch = (next: Partial<ScheduleDraft>) => props.onChange({ ...props.value, ...next });

    const toggleDay = (day: number) => patch({
        days: props.value.days.includes(day) ? props.value.days.filter(d => d !== day) : [...props.value.days, day],
    });

    const toggleDayOfMonth = (day: number) => patch({
        daysOfMonth: props.value.daysOfMonth.includes(day)
            ? props.value.daysOfMonth.filter(selected => selected !== day)
            : [...props.value.daysOfMonth, day],
    });

    const addTime = () => {
        const minutes = clockToMinutes(pendingTime());
        if (minutes == null || props.value.minutesOfDay.includes(minutes)) return;
        patch({ minutesOfDay: [...props.value.minutesOfDay, minutes].sort((a, b) => a - b) });
    };

    const removeTime = (minutes: number) => patch({ minutesOfDay: props.value.minutesOfDay.filter(m => m !== minutes) });

    const pendingTimeTaken = () => {
        const minutes = clockToMinutes(pendingTime());
        return minutes == null || props.value.minutesOfDay.includes(minutes);
    };

    return (
        <div class={styles.editor}>
            <DateRangePills<ScheduleKind>
                value={props.value.kind}
                onChange={(kind) => patch(kind === "weekly" ? { kind, days: [MONDAY_FIRST.find(day => props.value.days.includes(day)) ?? 1] } : { kind })}
                pills={[
                    { value: "times", labelKey: "AUTOMATION_SCHEDULE_KIND_TIMES" },
                    { value: "interval", labelKey: "AUTOMATION_SCHEDULE_KIND_INTERVAL" },
                    { value: "weekly", labelKey: "AUTOMATION_SCHEDULE_KIND_WEEKLY" },
                    { value: "monthly", labelKey: "AUTOMATION_SCHEDULE_KIND_MONTHLY" },
                ]}
            />

            <Show when={props.value.kind === "monthly"}>
                <div class={styles.group}>
                    <span class={styles.label}><Text val="AUTOMATION_SCHEDULE_DAYS_OF_MONTH" /></span>
                    <div class={styles.monthDays} role="group" aria-label={t("AUTOMATION_SCHEDULE_DAYS_OF_MONTH")}>
                        <For each={DAYS_OF_MONTH}>
                            {(day) => (
                                <button
                                    type="button"
                                    class={`${styles.day} ${styles.monthDay} ${props.value.daysOfMonth.includes(day) ? styles.dayActive : ""}`}
                                    aria-pressed={props.value.daysOfMonth.includes(day)}
                                    onClick={() => toggleDayOfMonth(day)}
                                >
                                    {day}
                                </button>
                            )}
                        </For>
                    </div>
                    <Show when={props.value.daysOfMonth.some(day => day > 28)}>
                        <span class={styles.hint}><Text val="AUTOMATION_SCHEDULE_SHORT_MONTH_HINT" /></span>
                    </Show>
                </div>
            </Show>

            <Show when={props.value.kind !== "monthly"}>
                <div class={styles.group}>
                    <div class={styles.groupHead}>
                        <span class={styles.label}><Text val="AUTOMATION_SCHEDULE_DAYS" /></span>
                        <Show when={props.value.kind !== "weekly"}>
                            <div class={styles.presets}>
                                <button type="button" class={styles.preset} onClick={() => patch({ days: [...MONDAY_FIRST] })}>
                                    <Text val="AUTOMATION_SCHEDULE_DAYS_EVERY" />
                                </button>
                                <button type="button" class={styles.preset} onClick={() => patch({ days: [...WEEKDAYS] })}>
                                    <Text val="AUTOMATION_SCHEDULE_DAYS_WEEKDAYS" />
                                </button>
                                <button type="button" class={styles.preset} onClick={() => patch({ days: [...WEEKEND] })}>
                                    <Text val="AUTOMATION_SCHEDULE_DAYS_WEEKEND" />
                                </button>
                            </div>
                        </Show>
                    </div>
                    <div class={styles.days} role="group" aria-label={t("AUTOMATION_SCHEDULE_DAYS")}>
                        <For each={MONDAY_FIRST}>
                            {(day) => (
                                <button
                                    type="button"
                                    class={`${styles.day} ${props.value.days.includes(day) ? styles.dayActive : ""}`}
                                    aria-pressed={props.value.days.includes(day)}
                                    onClick={() => props.value.kind === "weekly" ? patch({ days: [day] }) : toggleDay(day)}
                                >
                                    {t(`AUTOMATION_DAY_SHORT_${day}`)}
                                </button>
                            )}
                        </For>
                    </div>
                </div>
            </Show>

            <Show when={props.value.kind !== "interval"}>
                <div class={styles.group}>
                    <span class={styles.label}><Text val="AUTOMATION_SCHEDULE_TIMES" /></span>
                    <div class={styles.times}>
                        <Index each={props.value.minutesOfDay}>
                            {(minutes) => (
                                <span class={styles.timeChip}>
                                    {minutesToClock(minutes())}
                                    <button
                                        type="button"
                                        class={styles.timeRemove}
                                        title={t("AUTOMATION_SCHEDULE_TIME_REMOVE", minutesToClock(minutes()))}
                                        aria-label={t("AUTOMATION_SCHEDULE_TIME_REMOVE", minutesToClock(minutes()))}
                                        onClick={() => removeTime(minutes())}
                                    >
                                        <i class="fa-solid fa-xmark" />
                                    </button>
                                </span>
                            )}
                        </Index>
                        <div class={styles.timeAdd}>
                            <input
                                type="time"
                                value={pendingTime()}
                                aria-label={t("AUTOMATION_SCHEDULE_TIME_ADD")}
                                onInput={(e) => setPendingTime(e.currentTarget.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTime(); } }}
                            />
                            <button type="button" class="btn btn-secondary btn-sm" disabled={pendingTimeTaken()} onClick={addTime}>
                                <i class="fa-solid fa-plus" /> <Text val="AUTOMATION_SCHEDULE_TIME_ADD" />
                            </button>
                        </div>
                    </div>
                </div>
            </Show>

            <Show when={props.value.kind === "interval"}>
                <div class={styles.row}>
                    <label class={styles.field}>
                        <span class={styles.label}><Text val="AUTOMATION_SCHEDULE_INTERVAL" /></span>
                        <select
                            value={String(props.value.everyMinutes)}
                            onChange={(e) => patch({ everyMinutes: Number(e.currentTarget.value) })}
                        >
                            <For each={INTERVAL_PRESETS}>
                                {(minutes) => <option value={String(minutes)}>{intervalLabel(minutes, t)}</option>}
                            </For>
                        </select>
                    </label>
                    <label class={styles.field}>
                        <span class={styles.label}><Text val="AUTOMATION_SCHEDULE_FROM" /></span>
                        <input
                            type="time"
                            value={minutesToClock(props.value.fromMinute)}
                            onInput={(e) => {
                                const minutes = clockToMinutes(e.currentTarget.value);
                                if (minutes != null) patch({ fromMinute: minutes });
                            }}
                        />
                    </label>
                    <label class={styles.field}>
                        <span class={styles.label}><Text val="AUTOMATION_SCHEDULE_UNTIL" /></span>
                        <input
                            type="time"
                            value={minutesToClock(props.value.untilMinute)}
                            onInput={(e) => {
                                const minutes = clockToMinutes(e.currentTarget.value);
                                if (minutes != null) patch({ untilMinute: minutes });
                            }}
                        />
                    </label>
                </div>
                <Show when={props.value.untilMinute < props.value.fromMinute}>
                    <span class={styles.hint}>
                        <i class="fa-solid fa-moon" />{" "}
                        {t("AUTOMATION_SCHEDULE_OVERNIGHT_HINT", minutesToClock(props.value.untilMinute), minutesToClock(props.value.untilMinute), minutesToClock(props.value.fromMinute))}
                    </span>
                </Show>
            </Show>

            <Show when={!props.hideJitter}>
                <label class={styles.field}>
                    <span class={styles.label}><Text val="AUTOMATION_SCHEDULE_JITTER" /></span>
                    <input
                        type="number"
                        class={styles.jitter}
                        min={0}
                        max={MAX_JITTER}
                        value={props.value.jitterMinutes}
                        onInput={(e) => patch({ jitterMinutes: Math.min(MAX_JITTER, Math.max(0, Math.round(Number(e.currentTarget.value) || 0))) })}
                    />
                    <span class={styles.hint}><Text val="AUTOMATION_SCHEDULE_JITTER_HINT" /></span>
                </label>
            </Show>

            <div class={styles.preview}>
                <Show
                    when={!props.error}
                    fallback={
                        <div class={styles.problem}>
                            <i class="fa-solid fa-circle-exclamation" />
                            <span>{props.error}</span>
                        </div>
                    }
                >
                    <span class={styles.label}><Text val={props.upcomingKey ?? "AUTOMATION_SCHEDULE_UPCOMING"} /></span>
                    <Show
                        when={props.upcomingRuns.length > 0}
                        fallback={<i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />}
                    >
                        <ol class={styles.upcoming}>
                            <For each={props.upcomingRuns}>
                                {(run) => <li>{formatDateTimeShort(run)}</li>}
                            </For>
                        </ol>
                    </Show>
                    <Show when={props.value.jitterMinutes > 0 && !props.previewIncludesJitter}>
                        <span class={styles.hint}><Text val="AUTOMATION_SCHEDULE_UPCOMING_PLANNED" /></span>
                    </Show>
                </Show>
                <span class={styles.hint}>
                    <i class="fa-solid fa-clock" /> <Text val={props.clockHintKey ?? "AUTOMATION_SCHEDULE_CLOCK_HINT"} />
                </span>
            </div>
        </div>
    );
}
